import React from 'react';
import {
  Box,
  TextField,
  FormControl,
  InputLabel,
  Select,
  MenuItem,
  ToggleButton,
  ToggleButtonGroup,
  Paper,
  Typography,
} from '@mui/material';
import CategoryIcon from './CategoryIcon';
import { Category } from '../types/foundObject';
import { FilterState } from './FoundObjectsFilters';
import ReferenceSuffixFilterField from './ReferenceSuffixFilterField';
import CategoryAttributeFilterControls from './CategoryAttributeFilterControls';
import { useQuery } from '@tanstack/react-query';
import apiService from '../services/api';
import { useProviderInfo } from '../hooks/useProviderInfo';

function toISODateLocal(d: Date): string {
  const y = d.getFullYear();
  const m = String(d.getMonth() + 1).padStart(2, '0');
  const day = String(d.getDate()).padStart(2, '0');
  return `${y}-${m}-${day}`;
}

function todayLocal(): string {
  return toISODateLocal(new Date());
}

function addDaysLocal(iso: string, delta: number): string {
  const [y, m, d] = iso.split('-').map(Number);
  const dt = new Date(y, m - 1, d);
  dt.setDate(dt.getDate() + delta);
  return toISODateLocal(dt);
}

interface FoundObjectsTopFiltersProps {
  filters: FilterState;
  onFiltersChange: (f: FilterState) => void;
}

function computeDateToggleValue(f: FilterState): string | null {
  if (!f.dateFrom && !f.dateTo) {
    return 'all';
  }
  const t = todayLocal();
  if (f.dateFrom === t && f.dateTo === t) {
    return 'today';
  }
  const y = addDaysLocal(t, -1);
  if (f.dateFrom === y && f.dateTo === y) {
    return 'yesterday';
  }
  const b = addDaysLocal(t, -2);
  if (f.dateFrom === b && f.dateTo === b) {
    return 'before_yesterday';
  }
  const threeStart = addDaysLocal(t, -2);
  if (f.dateFrom === threeStart && f.dateTo === t) {
    return 'three_days';
  }

  return null;
}

/**
 * Bandeau de filtres au-dessus de la grille (recherche globale, ref, dates rapides, catégorie, conversations).
 */
const FoundObjectsTopFilters: React.FC<FoundObjectsTopFiltersProps> = ({ filters, onFiltersChange }) => {
  const { data: categoriesData = [] } = useQuery({
    queryKey: ['categories'],
    queryFn: () => apiService.getCategories(),
    select: (response) => response.data?.categories || [],
  });
  const categories = (categoriesData || []) as Category[];

  const { data: providerInfo } = useProviderInfo();

  const selectedCategory = filters.category
    ? categories.find((c) => c.name === filters.category)
    : undefined;

  const set = (patch: Partial<FilterState>) => {
    onFiltersChange({ ...filters, ...patch });
  };

  /** Une seule date affichée : jour unique si from===to, sinon fin de plage. */
  const displayedDay =
    filters.dateFrom && filters.dateTo && filters.dateFrom === filters.dateTo
      ? filters.dateFrom
      : filters.dateTo || filters.dateFrom || '';

  const setSingleDay = (iso: string) => {
    if (!iso) {
      set({ dateFrom: '', dateTo: '' });
      return;
    }
    set({ dateFrom: iso, dateTo: iso });
  };

  return (
    <Paper variant="outlined" sx={{ p: 2, mb: 2, borderRadius: 2 }}>
      <Typography variant="subtitle2" color="text.secondary" sx={{ mb: 1.5 }}>
        Filtres
      </Typography>

      <TextField
        fullWidth
        size="small"
        label="Recherche globale"
        placeholder="Titre, catégorie, attributs…"
        value={filters.search}
        onChange={(e) => set({ search: e.target.value })}
        sx={{ mb: 1.5 }}
      />

      <ReferenceSuffixFilterField
        value={filters.referenceCode}
        onChange={(suffix) => set({ referenceCode: suffix })}
        providerId={providerInfo?.id}
        sx={{ mb: 1.5 }}
      />

      <Box sx={{ mb: 1.5 }}>
        <Typography variant="caption" color="text.secondary" sx={{ display: 'block', mb: 0.5 }}>
          Quand ?
        </Typography>
        <Box
          sx={{
            display: 'flex',
            flexWrap: 'wrap',
            gap: 1,
            alignItems: 'center',
          }}
        >
          <TextField
            type="date"
            size="small"
            value={displayedDay}
            onChange={(e) => setSingleDay(e.target.value)}
            InputLabelProps={{ shrink: true }}
            sx={{ minWidth: 160 }}
            label="Date"
          />
          <ToggleButtonGroup
            exclusive
            size="small"
            value={computeDateToggleValue(filters)}
            onChange={(_, v) => {
              if (v == null) {
                return;
              }
              const t = todayLocal();
              if (v === 'all') {
                set({ dateFrom: '', dateTo: '' });
                return;
              }
              if (v === 'today') {
                set({ dateFrom: t, dateTo: t });
                return;
              }
              if (v === 'yesterday') {
                const y = addDaysLocal(t, -1);
                set({ dateFrom: y, dateTo: y });
                return;
              }
              if (v === 'before_yesterday') {
                const b = addDaysLocal(t, -2);
                set({ dateFrom: b, dateTo: b });
                return;
              }
              if (v === 'three_days') {
                set({ dateFrom: addDaysLocal(t, -2), dateTo: t });
              }
            }}
          >
            <ToggleButton value="today">Aujourd&apos;hui</ToggleButton>
            <ToggleButton value="yesterday">Hier</ToggleButton>
            <ToggleButton value="before_yesterday">Avant-hier</ToggleButton>
            <ToggleButton value="three_days">3 jours</ToggleButton>
            <ToggleButton value="all">Toutes</ToggleButton>
          </ToggleButtonGroup>
        </Box>
      </Box>

      <Box
        sx={{
          display: 'flex',
          flexWrap: 'wrap',
          gap: 2,
          alignItems: 'center',
        }}
      >
        <FormControl size="small" sx={{ minWidth: 220, flex: '1 1 200px' }}>
          <InputLabel>Catégories</InputLabel>
          <Select
            value={filters.category}
            label="Catégories"
            onChange={(e) =>
              set({
                category: e.target.value,
                attributes: {},
              })
            }
            renderValue={(selected) => {
              if (!selected) {
                return <em>Toutes</em>;
              }
              const cat = categories.find((c) => c.name === selected);
              return (
                <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                  <CategoryIcon
                    icon={cat?.icon}
                    customIconUrl={cat?.custom_icon_url}
                    customIconFilename={cat?.custom_icon_filename}
                    size="small"
                    color="primary.main"
                  />
                  {selected}
                </Box>
              );
            }}
          >
            <MenuItem value="">
              <em>Toutes</em>
            </MenuItem>
            {categories.map((category) => (
              <MenuItem key={category.id} value={category.name}>
                <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                  <CategoryIcon
                    icon={category.icon}
                    customIconUrl={category.custom_icon_url}
                    customIconFilename={category.custom_icon_filename}
                    size="small"
                    color="primary.main"
                  />
                  {category.name}
                </Box>
              </MenuItem>
            ))}
          </Select>
        </FormControl>
      </Box>

      {selectedCategory ? (
        <CategoryAttributeFilterControls
          category={selectedCategory}
          attributeFilters={filters.attributes}
          onAttributeFiltersChange={(attributes) => set({ attributes })}
          compact
        />
      ) : null}
    </Paper>
  );
};

export default FoundObjectsTopFilters;
